/**
 * Разделы кабинета и то, чем проверяется их доступность.
 *
 * Список вынесен из оболочки отдельно и намеренно ничего не импортирует из
 * React: по нему ходит не только меню, но и приёмка `tools/check-access.mjs`,
 * которая перебирает все четыре роли и сверяет состав меню с матрицей
 * контракта. Роль, которой по ошибке открыли лишний раздел, обнаруживается в
 * CI, а не у клиента агентства, увидевшего чужое семантическое ядро.
 *
 * У каждого раздела объявлены **путь и метод контракта**. Видимость считается
 * по ним, а не по списку ролей рядом с пунктом меню: список рядом с пунктом —
 * это вторая копия правды, которая расходится с сервером.
 *
 * **Название раздела — ключ каталога строк**, а не литерал (B5): меню обязано
 * переключаться вместе с языком кабинета. Само слово достаётся функциями
 * `sectionTitle`/`groupTitle` из `ui/labels.ts` в момент отрисовки — таблица с
 * готовыми строками посчиталась бы один раз, до того как известна локаль
 * вошедшего. Здесь этих функций нет намеренно: этот файл обязан оставаться без
 * единого импорта времени выполнения — его загружает голым Node приёмка
 * `check-access.mjs` (`--experimental-strip-types` стирает только типы).
 *
 * Порядок — по спеке §4, названия — по глоссарию voice.md.
 */

import type { IconName } from './ui/icons';

/**
 * Всё, что оболочка умеет закрыть: адрес, ключ его названия и вопрос к матрице.
 *
 * Раздел меню — частный случай; экраны вне меню (`OFF_MENU` ниже) описываются
 * тем же самым, потому что закрываются тем же самым.
 */
export interface Guarded {
  path: string;
  /** Ключ названия в каталоге строк. Слово — через `sectionTitle()`. */
  titleKey: string;
  /** Путь и метод контракта, которыми проверяется доступность. */
  needs: { method: string; path: string };
}

export interface Section extends Guarded {
  /**
   * Иконка раздела для свёрнутого меню (Lucide, `ui/icons.tsx`).
   *
   * Здесь стояло двухбуквенное сокращение — «Яд», «От», «Пс», — которое
   * невозможно расшифровать и которое заводило свой визуальный словарь вместо
   * набора, названного дизайн-системой. Иконка при этом слова не заменяет: у
   * ссылки остаётся полное название в `aria-label` и в тултипе.
   */
  icon: IconName;
  /** Экран собран. Остальные показывают честную пустоту. */
  ready?: boolean;
}

export interface SectionGroup {
  titleKey: string;
  sections: Section[];
}

export const GROUPS: SectionGroup[] = [
  {
    titleKey: 'section-group.scan',
    sections: [
      {
        path: '/',
        titleKey: 'section.overview',
        icon: 'gauge',
        needs: { method: 'GET', path: '/brands/{brandId}/overview' },
        ready: true,
      },
      {
        path: '/portfolio',
        titleKey: 'section.portfolio',
        icon: 'layout-grid',
        needs: { method: 'GET', path: '/portfolio' },
      },
      {
        // design: derived, not documented — раздела «Бренды» нет в перечне
        // спеки §4: там названы аналитические экраны. Но бренд по §5 — объект
        // со своим именем, вариантами написания, сайтом и городами,
        // и править их где-то надо: от полноты синонимов зависит знаменатель
        // всех метрик. Отсюда отдельный раздел — список, карточка, синонимы.
        path: '/brands',
        titleKey: 'section.brands',
        icon: 'tag',
        needs: { method: 'GET', path: '/brands' },
        ready: true,
      },
      {
        path: '/attention',
        titleKey: 'section.attention',
        icon: 'bell-ring',
        needs: { method: 'GET', path: '/alerts' },
        ready: true,
      },
      {
        path: '/engines',
        titleKey: 'section.engines',
        icon: 'cpu',
        needs: { method: 'GET', path: '/engines' },
        ready: true,
      },
      {
        // design: derived, not documented — раздела нет в перечне спеки §4, но
        // претензия клиента прямая: в кабинете не посмотреть, по каким промптам
        // видимость есть, а по каким нет. Плитка «видимость 21%» отвечает на
        // «сколько», а работают только с «где».
        path: '/visibility',
        titleKey: 'section.visibility',
        icon: 'scan-search',
        needs: { method: 'GET', path: '/brands/{brandId}/questions/visibility' },
        ready: true,
      },
      {
        // design: derived, not documented — такого раздела нет в списке спеки
        // §4. Название взято из глоссария voice.md («динамика»), новых терминов
        // не заводим. Ряд во времени при этом —
        // сердцевина продукта (§8 строит на нём динамику и волатильность, §9 —
        // довод «сделали и выросло»), и жить он может только на своём экране:
        // на обзоре ему нужен период, сравнение и суточные снимки.
        path: '/history',
        titleKey: 'section.history',
        icon: 'trending-up',
        needs: { method: 'GET', path: '/brands/{brandId}/metrics/daily' },
        ready: true,
      },
      {
        path: '/competitors',
        titleKey: 'section.competitors',
        icon: 'users',
        needs: { method: 'GET', path: '/brands/{brandId}/competitors' },
        ready: true,
      },
      {
        path: '/questions',
        titleKey: 'section.questions',
        icon: 'list-checks',
        needs: { method: 'GET', path: '/brands/{brandId}/questions' },
        ready: true,
      },
      {
        path: '/runs',
        titleKey: 'section.runs',
        icon: 'play',
        needs: { method: 'GET', path: '/runs' },
        ready: true,
      },
    ],
  },
  {
    titleKey: 'section-group.actions',
    sections: [
      {
        path: '/recommendations',
        titleKey: 'section.recommendations',
        icon: 'lightbulb',
        needs: { method: 'GET', path: '/brands/{brandId}/recommendations' },
        ready: true,
      },
      {
        path: '/plans',
        titleKey: 'section.plans',
        icon: 'map',
        needs: { method: 'GET', path: '/brands/{brandId}/action-plans' },
      },
      {
        path: '/sources',
        titleKey: 'section.sources',
        icon: 'link',
        needs: { method: 'GET', path: '/brands/{brandId}/metrics/sources' },
      },
      {
        path: '/alerts',
        titleKey: 'section.alerts',
        icon: 'bell',
        needs: { method: 'GET', path: '/alert-rules' },
        ready: true,
      },
      {
        path: '/reports',
        titleKey: 'section.reports',
        icon: 'file-text',
        needs: { method: 'GET', path: '/reports' },
        ready: true,
      },
    ],
  },
  {
    titleKey: 'section-group.organization',
    sections: [
      {
        // Клиенту агентства этот раздел не нужен по §5: у него ровно одно своё
        // пространство, и переключать нечего. Пункт при этом был единственным в
        // группе «Организация», кроме «Настроек», и вёл на заглушку «РАЗДЕЛ В
        // РАБОТЕ». Проверка идёт по правилу ЗАПИСИ, а не чтения: чтение
        // `/workspaces` матрица отдаёт и клиенту агентства — ради переключателя
        // области, а не ради раздела меню.
        path: '/workspaces',
        titleKey: 'section.workspaces',
        icon: 'folders',
        needs: { method: 'POST', path: '/workspaces' },
      },
      {
        path: '/members',
        titleKey: 'section.members',
        icon: 'user-round',
        needs: { method: 'GET', path: '/members' },
        ready: true,
      },
      {
        // Клиенты агентства — это те же пространства, но экран у них свой:
        // квота двигается между клиентами, и переключение на клиента меняет
        // область целиком. Наблюдателю и клиенту агентства раздел недоступен.
        path: '/clients',
        titleKey: 'section.clients',
        icon: 'briefcase',
        needs: { method: 'GET', path: '/agency/clients' },
        ready: true,
      },
      {
        path: '/plan',
        titleKey: 'section.plan',
        icon: 'credit-card',
        needs: { method: 'GET', path: '/billing/portal' },
        ready: true,
      },
      {
        path: '/activity',
        titleKey: 'section.activity',
        icon: 'history',
        needs: { method: 'GET', path: '/organizations/current/activity' },
      },
      {
        // Настройки видны всем ролям, но состоят у них из разного: организация
        // приходит только тому, кто до неё дотягивается. Клиенту агентства
        // остаются профиль, способы входа и его собственные сессии — иначе он
        // не сможет сменить себе пароль.
        path: '/settings',
        titleKey: 'section.settings',
        icon: 'settings',
        needs: { method: 'GET', path: '/settings/account' },
        ready: true,
      },
    ],
  },
];

/** Все разделы подряд — для маршрутизатора. */
export const SECTIONS: readonly Section[] = GROUPS.flatMap((group) => group.sections);

/**
 * Экраны кабинета, которых нет в меню, но которые оболочка обязана закрывать.
 *
 * Пункта меню у них нет потому, что заходят на них не из навигации, а по
 * событию: присвоение аудита открывается с публичного отчёта, приём владения —
 * из письма. Пункт «Присвоение аудита», висящий в меню постоянно, был бы
 * ссылкой, которая почти всегда ведёт в пустоту.
 *
 * Отсутствие пункта — не отсутствие проверки. Прямая ссылка обязана упереться в
 * ту же матрицу, что и меню: `POST /audits/claim` матрица отдаёт владельцу и
 * аналитику, значит наблюдатель и клиент агентства получают закрытый раздел, а
 * не форму, которая свалится в 403 после нажатия.
 *
 * `ALIASES` для этого не годятся: они привязывают адрес к **чужому** разделу, а
 * у присвоения своё правило контракта, не совпадающее ни с одним разделом меню.
 */
export const OFF_MENU: readonly Guarded[] = [
  {
    // Приём владения. Роль здесь — **любой участник**, и это не послабление:
    // на момент подтверждения преемник ещё не владелец, а требовать от него
    // роли владельца значило бы сделать передачу невыполнимой. В матрице путь
    // и стоит рядом с `/invitations/accept`.
    path: '/ownership-transfers',
    titleKey: 'section.ownership-transfers',
    needs: { method: 'POST', path: '/ownership-transfers/{transferId}/accept' },
  },
  {
    path: '/audits/claim',
    titleKey: 'section.audits-claim',
    needs: { method: 'POST', path: '/audits/claim' },
  },
];

/**
 * Адреса, живущие вне своего раздела, и раздел, которому они принадлежат.
 *
 * Возврат из ЮKassa приходит в письме от банка и открывается прямой ссылкой,
 * поэтому лежит не под `/plan`. Доступ у него тот же, что у тарифа: платит тот,
 * кто дотягивается до `GET /billing/portal`.
 */
const ALIASES: Record<string, string> = {
  '/billing/return': '/plan',
  // `/billing` руками набирают и по нему приходят со старых ссылок. Маршрута не
  // было, и человек получал «Такого раздела нет» на адрес раздела, который в
  // кабинете есть и называется «Тариф». Теперь адрес уводит туда же, и закрыт
  // он тем же правом.
  '/billing': '/plan',
  // Мастер первого бренда живёт вне раздела: после регистрации разделов ещё
  // нет, а есть один вопрос — какой у вас сайт. Доступ у него тот же, что у
  // брендов, иначе наблюдатель, которому прислали ссылку, попадёт в форму
  // заведения бренда и упрётся в отказ уже после заполнения.
  '/onboarding': '/brands',
};

/**
 * Раздел, которому принадлежит адрес кабинета.
 *
 * Нужен оболочке, чтобы закрывать раздел целиком, а не только прятать его из
 * меню. Спрятанный пункт закрывает меню, но не адресную строку: ссылка на
 * `/members`, присланная коллегой, обязана упереться в то же правило матрицы,
 * что и меню, иначе наблюдатель увидит кнопки «Пригласить» и «Отозвать
 * доступ» — то есть действия, которых у него нет.
 *
 * Совпадение ищется по самому длинному префиксу, поэтому вложенные адреса
 * (`/plan/checkout`) наследуют доступ своего раздела и новый вложенный экран
 * закрывается сам, без правки этого файла.
 */
export function sectionFor(pathname: string): Guarded | undefined {
  const path = ALIASES[pathname] ?? pathname;
  if (path === '/') return SECTIONS.find((section) => section.path === '/');

  let best: Guarded | undefined;
  for (const section of [...SECTIONS, ...OFF_MENU]) {
    // Корень совпал бы с любым адресом и увёл бы проверку не в тот раздел.
    if (section.path === '/') continue;
    if (path !== section.path && !path.startsWith(`${section.path}/`)) continue;
    if (!best || section.path.length > best.path.length) best = section;
  }
  return best;
}
